import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import {
  FaPencilAlt,
  FaTimes,
  FaPhoneAlt,
  FaEnvelope,
  FaLandmark,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserInfo } from '../../store/features/userInfo';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axios';

import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';

function UserProfile() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, userInfo } = useSelector((state) => state.userInfo);

  const [editInfo, setEditInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    user_id: id,
    first_name: '',
    last_name: '',
    mobile_no: '',
    email_id: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
  });

  useEffect(() => {
    dispatch(fetchUserInfo({ id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (userInfo) {
      // Pre-format the mobile number so PhoneInput recognizes Indian numbers stored without +91
      let initialMobile = userInfo.mobile_no || '';
      if (initialMobile && !initialMobile.startsWith('+') && initialMobile.length === 10) {
        initialMobile = '+91' + initialMobile;
      }

      setFormData({
        user_id: id,
        first_name: userInfo.first_name || '',
        last_name: userInfo.last_name || '',
        mobile_no: initialMobile,
        email_id: userInfo.email_id || '',
        address: userInfo.address || '',
        pincode: userInfo.pincode || '',
        city: userInfo.city || '',
        state: userInfo.state || '',
      });
    }
  }, [userInfo, id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Phone Validation Check ---
    if (formData.mobile_no && !isValidPhoneNumber(formData.mobile_no)) {
      alert('Please enter a valid international mobile number.');
      return;
    }

    setIsSubmitting(true);

    // --- Strip +91 before sending to backend ---
    let formattedMobile = formData.mobile_no;
    if (formattedMobile && formattedMobile.startsWith('+91')) {
      formattedMobile = formattedMobile.replace('+91', '');
    }

    const payload = {
        ...formData,
        mobile_no: formattedMobile,
        user_id: id 
      };

    try {
      console.log("Request body:", { data: payload });
      await axiosInstance.put(`super-admin/user/details/${id}`, { data: payload });
      
      dispatch(fetchUserInfo({ id }));
      
      setEditInfo(false);
    } catch (error) {
      console.error('Error updating user profile:', error);
      alert(error.response?.data?.msg || 'Failed to update profile.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wrapper>
      {status === 'loading' ? (
        <h1>Loading...</h1>
      ) : (
        <div className='profile-page w-100'>
          <Row>
            {/* LEFT PROFILE CARD */}
            <Col md={6}>
              <Card className='p-0 rounded-4'>
                <Card.Body className='p-0'>
                  
                  {/* HEADER */}
                  <div className='row align-items-center p-md-5 p-4'>
                    <div className='col-md-auto text-center mb-3 mb-md-0'>
                      <div
                        className='rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bolder mx-auto'
                        style={{
                          width: '140px',
                          height: '140px',
                          fontSize: '56px',
                        }}
                      >
                        {(userInfo.first_name ? userInfo.first_name[0] : '') +
                          (userInfo.last_name ? userInfo.last_name[0] : '')}
                      </div>
                    </div>
                    <div className='col-md col-xs-12 text-center'>
                      <h1 className='fw-bold text-md-start'>
                        {userInfo.first_name} {userInfo.last_name}
                      </h1>
                      <p className='text-muted text-md-start m-0'>
                        Joined on{' '}
                        {new Date(userInfo.created_at).toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>

                  {/* DETAILS & INLINE EDIT */}
                  <div className='bg-light rounded-bottom-4 p-md-5 p-4'>
                    <div className='position-relative'>
                      {/* TOGGLE ICONS */}
                      <div
                        className='position-absolute top-0 end-0 p-0'
                        style={{
                          marginTop: `${editInfo ? '-10px' : '-32px'}`,
                          marginRight: '-10px',
                          cursor: 'pointer',
                        }}
                      >
                        {!editInfo && (
                          <FaPencilAlt
                            size={18}
                            className="text-secondary"
                            onClick={() => setEditInfo(true)}
                          />
                        )}
                        {editInfo && (
                          <FaTimes
                            size={28}
                            className="text-secondary"
                            onClick={() => {
                              // Reset logic
                              let resetMobile = userInfo.mobile_no || '';
                              if (resetMobile && !resetMobile.startsWith('+') && resetMobile.length === 10) {
                                resetMobile = '+91' + resetMobile;
                              }

                              setFormData({
                                user_id: id,
                                first_name: userInfo.first_name || '',
                                last_name: userInfo.last_name || '',
                                mobile_no: resetMobile,
                                email_id: userInfo.email_id || '',
                                address: userInfo.address || '',
                                pincode: userInfo.pincode || '',
                                city: userInfo.city || '',
                                state: userInfo.state || '',
                              });
                              setEditInfo(false);
                            }}
                          />
                        )}
                      </div>

                      {!editInfo && (
                        <div className='user-info fs-5'>
                          <Row className='my-4'>
                            <Col className='col-auto'><FaPhoneAlt /></Col>
                            {/* Dynamically display +91 only if it's an Indian number missing the country code */}
                            <Col style={{ wordBreak: 'break-all' }}>
                              {userInfo.mobile_no?.startsWith('+') 
                                ? userInfo.mobile_no 
                                : `+91 ${userInfo.mobile_no}`}
                            </Col>
                          </Row>
                          <Row className='my-4'>
                            <Col className='col-auto'><FaEnvelope /></Col>
                            <Col style={{ wordBreak: 'break-all' }}>
                              {userInfo.email_id !== '' ? userInfo.email_id : 'NA'}
                            </Col>
                          </Row>
                          <Row className='my-4'>
                            <Col className='col-auto'><FaLandmark /></Col>
                            <Col style={{ wordBreak: 'break-all' }}>{userInfo.address}</Col>
                          </Row>
                          <Row className='my-4'>
                            <Col className='col-auto'><FaMapMarkerAlt /></Col>
                            <Col style={{ wordBreak: 'break-all' }}>
                              {userInfo.city}, {userInfo.state} {userInfo.pincode}
                            </Col>
                          </Row>
                        </div>
                      )}

                      {editInfo && (
                        <div className='user-info-edit mt-4'>
                          <Form onSubmit={handleSubmit}>
                            <Row className="g-3">
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">First Name</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    placeholder="First Name"
                                    required
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">Last Name</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    placeholder="Last Name"
                                    required
                                  />
                                </Form.Group>
                              </Col>

                              {/* --- UPDATED MOBILE FIELD --- */}
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">Mobile No.</Form.Label>
                                  <PhoneInput
                                    international
                                    defaultCountry="IN"
                                    value={formData.mobile_no}
                                    onChange={(val) => setFormData({ ...formData, mobile_no: val || '' })}
                                    className="form-control d-flex align-items-center"
                                  />
                                </Form.Group>
                              </Col>
                              
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">Email</Form.Label>
                                  <Form.Control
                                    type="email"
                                    name="email_id"
                                    value={formData.email_id}
                                    onChange={handleChange}
                                    placeholder="Enter email"
                                  />
                                </Form.Group>
                              </Col>

                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">Address</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Enter address"
                                    required
                                  />
                                </Form.Group>
                              </Col>

                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">Zip / Postal Code</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="pincode"
                                    value={formData.pincode}
                                    onChange={handleChange}
                                    placeholder="Enter Zip/Postal Code"
                                    required
                                  />
                                </Form.Group>
                              </Col>

                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">City</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                    required
                                  />
                                </Form.Group>
                              </Col>

                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold text-muted mb-1">State</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    placeholder="State"
                                    required
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            <div className="mt-4 d-grid">
                              <Button type="submit" variant="dark" disabled={isSubmitting}>
                                {isSubmitting ? 'Updating...' : 'Save Changes'}
                              </Button>
                            </div>
                          </Form>
                        </div>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* RIGHT ACTIONS CARD */}
            <Col md={6}>
              <Card className='rounded-4'>
                <Card.Body className=''>
                  <h3 className='fw-bold text-center pt-0'>User's activities</h3>
                  <Col>
                    <Card className='text-center border-0'>
                      <Card.Body>
                        <Button
                          variant="info"
                          className='w-75 mt-2'
                          onClick={() => navigate(`/users/bookings/${id}`)}
                        >
                          Bookings
                        </Button>
                        <Button
                          variant="success"
                          className='w-75 mt-2'
                          onClick={() => navigate(`/users/reports/${id}`)}
                        >
                          Reports
                        </Button>
                        <Button
                          variant="warning"
                          className='w-75 mt-2'
                          onClick={() => navigate(`/users/farms/${id}`)}
                        >
                          Farms
                        </Button>
                        <Button
                          variant="primary"
                          className='w-75 mt-2'
                          onClick={() => navigate(`/user/${id}/practice/list`)}
                        >
                          Practice
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </Wrapper>
  );
}

export default UserProfile;